/**
 * Billing View
 *
 * Admin functionality for managing billing operations:
 * - Export transactions to CSV with date range & attorney selection
 * - Add new matters to the system
 */

import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Download,
  Plus,
  Clock,
  Users,
  X,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { useProfessionals, useExportTransactions, useAddMatter } from "../hooks/useAdminData";
import { MatterFormValues, validateMatterForm } from "../utils/adminHelpers";
import styles from "./AdminViews.module.css";

export default function BillingView() {
  // ─────────────────────────────────────────────
  // Professionals data
  // ─────────────────────────────────────────────
  const { professionals, loading: loadingProfessionals } = useProfessionals();

  // ─────────────────────────────────────────────
  // Export Transactions state
  // ─────────────────────────────────────────────
  const [selectedProfIds, setSelectedProfIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: undefined, to: undefined });
  const [singleDate, setSingleDate] = useState<Date | undefined>(undefined);
  const [exportError, setExportError] = useState<string | null>(null);

  const {
    loading: exportLoading,
    previewLoading,
    previewResult,
    error: exportPreviewError,
    clearPreview,
    previewHours,
    exportToCsv,
  } = useExportTransactions();

  // Clear preview when selections change
  useEffect(() => {
    clearPreview();
  }, [dateRange, singleDate, selectedProfIds, clearPreview]);

  // ─────────────────────────────────────────────
  // Add Matter state
  // ─────────────────────────────────────────────
  const [matterFormValues, setMatterFormValues] = useState<MatterFormValues>({
    matter_id: "",
    client: "",
    description: "",
    matter: "",
  });
  const [matterFieldErrors, setMatterFieldErrors] = useState<
    Record<string, string>
  >({});
  const [showMatterConfirmation, setShowMatterConfirmation] = useState(false);

  const { loading: addMatterLoading, addMatter } = useAddMatter();

  // ─────────────────────────────────────────────
  // Export handlers
  // ─────────────────────────────────────────────
  const toggleProfessional = (profId: string) => {
    setSelectedProfIds((prev) =>
      prev.includes(profId)
        ? prev.filter((id) => id !== profId)
        : [...prev, profId]
    );
  };

  const selectAllProfessionals = () => {
    if (selectedProfIds.length === professionals.length) {
      setSelectedProfIds([]);
    } else {
      setSelectedProfIds(professionals.map((p) => p.prof_id));
    }
  };

  const handlePreviewHours = async () => {
    const startDate = dateRange.from || singleDate;
    const endDate = dateRange.to || dateRange.from || singleDate;
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (!startDate) {
      setExportError("Please select a date or date range first");
      return;
    }

    if (startDate > today || (endDate && endDate > today)) {
      setExportError("Date range cannot include future dates");
      return;
    }

    if (selectedProfIds.length === 0) {
      setExportError("Please select at least one attorney");
      return;
    }

    setExportError(null);
    await previewHours(
      format(startDate, "yyyy-MM-dd"),
      format(endDate!, "yyyy-MM-dd"),
      selectedProfIds
    );
  };

  const handleExport = async () => {
    const startDate = dateRange.from || singleDate;
    const endDate = dateRange.to || dateRange.from || singleDate;
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (!startDate) {
      setExportError("Please select a date");
      return;
    }

    if (startDate > today || (endDate && endDate > today)) {
      setExportError("Date range cannot include future dates");
      return;
    }

    if (selectedProfIds.length === 0) {
      setExportError("Please select at least one attorney");
      return;
    }

    setExportError(null);
    await exportToCsv(
      format(startDate, "yyyy-MM-dd"),
      format(endDate!, "yyyy-MM-dd"),
      selectedProfIds
    );
  };

  // ─────────────────────────────────────────────
  // Add Matter handlers
  // ─────────────────────────────────────────────
  const handleAddMatterClick = () => {
    const errors = validateMatterForm(matterFormValues);
    setMatterFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      setShowMatterConfirmation(true);
    }
  };

  const handleConfirmAddMatter = async () => {
    setShowMatterConfirmation(false);
    const result = await addMatter(matterFormValues);

    if (result.success) {
      setMatterFormValues({
        matter_id: "",
        client: "",
        description: "",
        matter: "",
      });
      setMatterFieldErrors({});
    } else if (result.error?.includes("already exists")) {
      setMatterFieldErrors({ matter_id: result.error });
    }
  };

  const clearMatterForm = () => {
    setMatterFormValues({
      matter_id: "",
      client: "",
      description: "",
      matter: "",
    });
    setMatterFieldErrors({});
  };

  return (
    <div className={styles.adminViewWrapper}>
      {/* Error Modal */}
      {(exportError || exportPreviewError) && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setExportError(null);
            clearPreview();
          }}
        >
          <div
            className={cn(styles.modal, "max-w-md text-center")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-red-500 mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-lg text-gray-800 whitespace-pre-line leading-relaxed">
              {exportError || exportPreviewError}
            </p>
            <Button
              className="mt-6"
              onClick={() => {
                setExportError(null);
                clearPreview();
              }}
            >
              OK
            </Button>
          </div>
        </div>
      )}

      {/* Add Matter Confirmation Modal */}
      {showMatterConfirmation && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowMatterConfirmation(false)}
        >
          <div
            className={cn(styles.modal, "max-w-lg")}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Add Matter
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please review the matter details before adding:
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
              <div className="flex">
                <span className="text-sm font-medium text-gray-500 w-28">
                  Matter ID:
                </span>
                <span className="text-sm text-gray-900">
                  {matterFormValues.matter_id}
                </span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium text-gray-500 w-28">
                  Client:
                </span>
                <span className="text-sm text-gray-900">
                  {matterFormValues.client}
                </span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium text-gray-500 w-28">
                  Description:
                </span>
                <span className="text-sm text-gray-900">
                  {matterFormValues.description}
                </span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium text-gray-500 w-28">
                  Matter:
                </span>
                <span className="text-sm text-gray-900">
                  {matterFormValues.matter}
                </span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowMatterConfirmation(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleConfirmAddMatter}>Confirm & Add</Button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.settingsContainer}>
        {/* Top Row: Export + Add Matter side-by-side */}
        <div className={styles.settingsTopRow}>
          {/* Export Transactions Section */}
          <div className={styles.headerSection}>
            <h2 className={styles.settingsSectionHeader}>
              Export Transactions
            </h2>
            <div className={styles.settingsSectionContent} style={{ marginTop: 20 }}>
                {/* Date Range */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">
                      Date Range
                    </label>
                    {(dateRange.from || dateRange.to || singleDate) && (
                      <button
                        onClick={() => {
                          setDateRange({ from: undefined, to: undefined });
                          setSingleDate(undefined);
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !dateRange.from && !singleDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.from || dateRange.to ? (
                          <span className="truncate">
                            {dateRange.from && dateRange.to
                              ? `${dateRange.from.toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })} - ${dateRange.to.toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}`
                              : dateRange.from
                              ? `${dateRange.from.toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })} - End`
                              : `Start - ${dateRange.to?.toLocaleDateString(
                                  undefined,
                                  { month: "short", day: "numeric", year: "numeric" }
                                )}`}
                          </span>
                        ) : singleDate ? (
                          <span className="truncate">
                            {singleDate.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        ) : (
                          "Select date range"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="p-3 space-y-2">
                        <div className="flex flex-row gap-2 mb-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-accentblue text-accentblue hover:bg-accentsoft/20"
                            onClick={() => {
                              setDateRange({ from: undefined, to: undefined });
                              setSingleDate(new Date());
                            }}
                          >
                            Today
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-accentblue text-accentblue hover:bg-accentsoft/20"
                            onClick={() => {
                              const today = new Date();
                              const dayOfWeek = today.getDay();
                              const monday = new Date(today);
                              monday.setDate(
                                today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)
                              );
                              const friday = new Date(monday);
                              friday.setDate(monday.getDate() + 4);
                              const endDay = friday > today ? today : friday;
                              setDateRange({ from: monday, to: endDay });
                              setSingleDate(undefined);
                            }}
                          >
                            Work Week
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-accentblue text-accentblue hover:bg-accentsoft/20"
                            onClick={() => {
                              const today = new Date();
                              const firstDay = new Date(
                                today.getFullYear(),
                                today.getMonth(),
                                1
                              );
                              const lastDay = new Date(
                                today.getFullYear(),
                                today.getMonth() + 1,
                                0
                              );
                              const endDay = lastDay > today ? today : lastDay;
                              setDateRange({ from: firstDay, to: endDay });
                              setSingleDate(undefined);
                            }}
                          >
                            This Month
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setDateRange({ from: undefined, to: undefined });
                              setSingleDate(new Date());
                            }}
                            className={cn(
                              "px-3 py-1 text-sm rounded border",
                              singleDate && !dateRange.from && !dateRange.to
                                ? "bg-black text-white border-gray-700"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            )}
                          >
                            Single Date
                          </button>
                          <button
                            onClick={() => {
                              const today = singleDate || new Date();
                              setDateRange({ from: today, to: today });
                              setSingleDate(undefined);
                            }}
                            className={cn(
                              "px-3 py-1 text-sm rounded border",
                              dateRange.from || dateRange.to
                                ? "bg-black text-white border-gray-700"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            )}
                          >
                            Date Range
                          </button>
                        </div>
                        {dateRange.from || dateRange.to ? (
                          <Calendar
                            mode="range"
                            selected={
                              dateRange as { from: Date | undefined; to: Date | undefined }
                            }
                            onSelect={(range) => {
                              if (range) {
                                setDateRange({ from: range.from, to: range.to });
                              }
                            }}
                            disabled={{ after: new Date() }}
                            initialFocus
                          />
                        ) : (
                          <Calendar
                            mode="single"
                            selected={singleDate}
                            onSelect={(d) => setSingleDate(d || undefined)}
                            disabled={{ after: new Date() }}
                            initialFocus
                          />
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Attorneys Multi-select */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">
                    Attorneys
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-auto min-h-[40px] py-2",
                          selectedProfIds.length === 0 && "text-muted-foreground"
                        )}
                      >
                        <Users className="mr-2 h-4 w-4 flex-shrink-0" />
                        {loadingProfessionals ? (
                          "Loading..."
                        ) : selectedProfIds.length === 0 ? (
                          "Select attorneys..."
                        ) : selectedProfIds.length <= 3 ? (
                          <span className="truncate">
                            {professionals
                              .filter((p) => selectedProfIds.includes(p.prof_id))
                              .map((p) => p.full_name.split(" ")[0])
                              .join(", ")}
                          </span>
                        ) : (
                          `${selectedProfIds.length} attorneys selected`
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <div className="p-2 border-b flex items-center justify-between">
                        <span className="text-sm font-medium">
                          Select Attorneys
                        </span>
                        <div className="flex gap-1">
                          {selectedProfIds.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedProfIds([])}
                              className="h-7 text-xs"
                            >
                              Clear
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={selectAllProfessionals}
                            className="h-7 text-xs"
                          >
                            {selectedProfIds.length === professionals.length
                              ? "None"
                              : "All"}
                          </Button>
                        </div>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto p-2">
                        {professionals.map((prof) => (
                          <label
                            key={prof.prof_id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedProfIds.includes(prof.prof_id)}
                              onChange={() => toggleProfessional(prof.prof_id)}
                              className="h-4 w-4 rounded border-gray-300 flex-shrink-0"
                            />
                            <span className="text-sm">
                              {prof.full_name || prof.prof_id}
                            </span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {selectedProfIds.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {professionals
                        .filter((p) => selectedProfIds.includes(p.prof_id))
                        .slice(0, 5)
                        .map((prof) => (
                          <span
                            key={prof.prof_id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-xs"
                          >
                            {prof.full_name.split(" ")[0]}
                            <button
                              onClick={() => toggleProfessional(prof.prof_id)}
                              className="hover:bg-accent rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      {selectedProfIds.length > 5 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-xs text-muted-foreground">
                          +{selectedProfIds.length - 5} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Preview Hours */}
                <div className="mb-4 flex flex-col items-center">
                  <Button
                    variant="outline"
                    onClick={handlePreviewHours}
                    disabled={
                      previewLoading ||
                      (!dateRange.from && !singleDate) ||
                      selectedProfIds.length === 0
                    }
                    className="w-full bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-700"
                  >
                    <Clock className="mr-2 h-4 w-4" />
                    {previewLoading ? "Loading..." : "Preview Hours"}
                  </Button>
                  {previewResult && (
                    <div className={cn(styles.previewBox, "w-full")}>
                      <p className={styles.previewText}>
                        {previewResult.total} hours across {previewResult.count}{" "}
                        {previewResult.count === 1 ? "entry" : "entries"} from{" "}
                        {previewResult.attorneys}{" "}
                        {previewResult.attorneys === 1 ? "attorney" : "attorneys"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Export Button */}
                <Button
                  onClick={handleExport}
                  disabled={
                    exportLoading ||
                    (!dateRange.from && !singleDate) ||
                    selectedProfIds.length === 0
                  }
                  className="w-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {exportLoading ? "Exporting..." : "Export to CSV"}
                </Button>
            </div>
          </div>

          {/* Add Matter Section */}
          <div className={styles.headerSection}>
            <h2 className={styles.settingsSectionHeader}>Add Matter</h2>
            <div className={styles.settingsSectionContent} style={{ marginTop: 20 }}>
                {/* Matter ID */}
                <div className="mb-4">
                  <Label className="block text-sm font-medium mb-2">
                    Matter ID
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter matter ID..."
                    value={matterFormValues.matter_id}
                    onChange={(e) => {
                      setMatterFormValues((prev) => ({
                        ...prev,
                        matter_id: e.target.value,
                      }));
                      if (matterFieldErrors.matter_id) {
                        setMatterFieldErrors((prev) => ({
                          ...prev,
                          matter_id: "",
                        }));
                      }
                    }}
                    className={matterFieldErrors.matter_id ? styles.inputError : ""}
                  />
                  {matterFieldErrors.matter_id && (
                    <p className={styles.fieldError}>
                      {matterFieldErrors.matter_id}
                    </p>
                  )}
                </div>

                {/* Client */}
                <div className="mb-4">
                  <Label className="block text-sm font-medium mb-2">Client</Label>
                  <Textarea
                    placeholder="Enter client name..."
                    value={matterFormValues.client}
                    onChange={(e) => {
                      setMatterFormValues((prev) => ({
                        ...prev,
                        client: e.target.value,
                      }));
                      if (matterFieldErrors.client) {
                        setMatterFieldErrors((prev) => ({ ...prev, client: "" }));
                      }
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    className={cn(
                      "min-h-[40px] resize-none overflow-hidden",
                      matterFieldErrors.client && styles.inputError
                    )}
                    rows={1}
                  />
                  {matterFieldErrors.client && (
                    <p className={styles.fieldError}>{matterFieldErrors.client}</p>
                  )}
                </div>

                {/* Description */}
                <div className="mb-4">
                  <Label className="block text-sm font-medium mb-2">
                    Description
                  </Label>
                  <Textarea
                    placeholder="Enter description..."
                    value={matterFormValues.description}
                    onChange={(e) => {
                      setMatterFormValues((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                      if (matterFieldErrors.description) {
                        setMatterFieldErrors((prev) => ({
                          ...prev,
                          description: "",
                        }));
                      }
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    className={cn(
                      "min-h-[40px] resize-none overflow-hidden",
                      matterFieldErrors.description && styles.inputError
                    )}
                    rows={1}
                  />
                  {matterFieldErrors.description && (
                    <p className={styles.fieldError}>
                      {matterFieldErrors.description}
                    </p>
                  )}
                </div>

                {/* Matter */}
                <div className="mb-6">
                  <Label className="block text-sm font-medium mb-2">Matter</Label>
                  <Textarea
                    placeholder="Enter matter name..."
                    value={matterFormValues.matter}
                    onChange={(e) => {
                      setMatterFormValues((prev) => ({
                        ...prev,
                        matter: e.target.value,
                      }));
                      if (matterFieldErrors.matter) {
                        setMatterFieldErrors((prev) => ({ ...prev, matter: "" }));
                      }
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    className={cn(
                      "min-h-[40px] resize-none overflow-hidden",
                      matterFieldErrors.matter && styles.inputError
                    )}
                    rows={1}
                  />
                  {matterFieldErrors.matter && (
                    <p className={styles.fieldError}>{matterFieldErrors.matter}</p>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddMatterClick}
                    disabled={addMatterLoading}
                    className="w-full sm:w-auto"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {addMatterLoading ? "Adding..." : "Add Matter"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={clearMatterForm}
                    disabled={addMatterLoading}
                    className="w-full sm:w-auto"
                  >
                    Clear
                  </Button>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
